import React, { useState, useEffect, useRef } from 'react'
import { ChevronDown, Trophy, Swords } from 'lucide-react'
import { useAccount, useChainId, useSwitchChain } from 'wagmi'
import { usePlayerStatus } from '../hooks/useOGBToken'
import { useBackendStatus } from '../hooks/useBackendStatus'
import { useWebSocket } from '../hooks/useWebSocket'
import { useDebugSettings } from '../hooks/useDebugSettings'
import { formatOgbBalanceDisplay } from '../utils/baseAppUtils'
import { playSoundSafe } from '../utils/audio'
import { baseSepolia, base } from '../config/networks'
import { MusicButton } from './MusicButton'

// AGC: P2P Pending Match data structure
interface P2PPendingMatch {
  battleId: string
  opponentAddress: string
  opponentLevel: number
  hasPaid: boolean
  timestamp: number
  expiresAt: number // 3 minute timeout
}

// AGC: P2P Active Battle data structure (in-battle state)
interface P2PActiveBattle {
  battleId: string
  opponentAddress: string
  opponentLevel: number
  selectedBuffs: number[]
  selectedPotion: number | null
  currentTurn: number
  turnTimeLeft: number
  timestamp: number
}

interface Tournament {
  id: string
  status: 'WAITING_FOR_PLAYERS' | 'COUNTDOWN' | 'RUNNING' | 'FINISHED'
  minPlayersToStart: number
  maxPlayers: number
  createdAt: number
  autoStartAt: number
  countdownEndsAt?: number
  rewards: {
    first: { ogb: number; potions: number; nftItem?: string }
    second: { ogb: number; potions: number }
    third: { ogb: number; potions: number }
  }
}

interface Participant {
  playerId: string
  level: number
  totalBattles: number
  winRate: number
  joinedAt: number
  isReady: boolean
  isActive: boolean
}

interface TopNavProps {
  onOpenSuggestion: () => void
  onOpenProfile: () => void // AGC: Open Profile page
  onOpenSettings: () => void
  onOpenInventory: () => void
  onOpenTournament: () => void
  onSelectTournament?: (tournamentId: string | null) => void // AGC: Callback to set tournament ID (for "Join Contest" popup)
  activePage?: string // AGC: Track page changes to refresh tournament check
  onOpenEmergencyWithdrawStakingManager?: () => void // AGC: Navigate to StakingManager emergency withdraw
  onOpenEmergencyWithdrawStakingRevenueShare?: () => void // AGC: Navigate to StakingRevenueShare emergency withdraw
  onOpenP2PBattle?: () => void // AGC: Navigate to P2P battle page (for pending match indicator)
  p2pPendingMatch?: P2PPendingMatch | null // AGC: P2P pending match data for minimize indicator
  p2pActiveBattle?: P2PActiveBattle | null // AGC: P2P active battle data for in-battle minimize indicator
}

/**
 * AGC: Shared top navigation bar for Base App screens.
 * AGC: Displays suggestion button, settings button, network status, OGB balance pill, and tournament popup.
 */
export const TopNav: React.FC<TopNavProps> = ({
  onOpenSuggestion,
  onOpenProfile,
  onOpenSettings,
  onOpenInventory,
  onOpenTournament,
  onSelectTournament,
  activePage,
  onOpenEmergencyWithdrawStakingManager,
  onOpenEmergencyWithdrawStakingRevenueShare,
  onOpenP2PBattle,
  p2pPendingMatch,
  p2pActiveBattle,
}) => {
  const { address } = useAccount()
  const chainId = useChainId()
  const { switchChain, isPending: isSwitchingChain } = useSwitchChain()
  const { balance: ogbBalance } = usePlayerStatus(address)
  const { isOnline, isChecking } = useBackendStatus()
  const { socket } = useWebSocket()
  const [tournament, setTournament] = useState<Tournament | null>(null)
  const [participants, setParticipants] = useState<Participant[]>([])
  const [showPopup, setShowPopup] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState<number>(0)
  const [isHiding, setIsHiding] = useState(false)
  const [showNetworkDropdown, setShowNetworkDropdown] = useState(false)
  const networkDropdownRef = useRef<HTMLDivElement>(null)
  const [isMusicPopupExpanded, setIsMusicPopupExpanded] = useState(true) // AGC: Track if music popup is expanded
  
  // AGC: Emergency withdraw popup visibility (default: hidden) - synced via WebSocket
  const { debugSettings } = useDebugSettings()
  const stakingManagerButtonEnabled = debugSettings.showEmergencyWithdrawStakingManager
  const stakingRevenueShareButtonEnabled = debugSettings.showEmergencyWithdrawStakingRevenueShare
  const anyButtonEnabled = stakingManagerButtonEnabled || stakingRevenueShareButtonEnabled
  
  // AGC: Emergency withdraw popup state (like tournament popup)
  const [showEmergencyWithdrawPopup, setShowEmergencyWithdrawPopup] = useState(false)
  const [isEmergencyWithdrawHiding, setIsEmergencyWithdrawHiding] = useState(false)
  
  // AGC: Show emergency withdraw popup if any button is enabled and not dismissed (like tournament popup)
  useEffect(() => {
    const hasCallbacks = onOpenEmergencyWithdrawStakingManager || onOpenEmergencyWithdrawStakingRevenueShare
    if (!anyButtonEnabled || !hasCallbacks) {
      setShowEmergencyWithdrawPopup(false)
      return
    }
    
    // AGC: Check if dismissed in sessionStorage (clears on refresh)
    const dismissed = sessionStorage.getItem('emergency_withdraw_popup_dismissed')
    if (dismissed !== 'true') {
      setShowEmergencyWithdrawPopup(true)
    }
  }, [anyButtonEnabled, onOpenEmergencyWithdrawStakingManager, onOpenEmergencyWithdrawStakingRevenueShare])
  
  // AGC: Clear dismissal flag on page load/refresh (like tournament popup)
  useEffect(() => {
    const pageLoadTimestamp = sessionStorage.getItem('page_load_timestamp')
    const currentTimestamp = Date.now().toString()
    
    if (!pageLoadTimestamp || (Date.now() - parseInt(pageLoadTimestamp)) > 1000) {
      sessionStorage.removeItem('emergency_withdraw_popup_dismissed')
      sessionStorage.setItem('page_load_timestamp', currentTimestamp)
    }
  }, [])
  
  // AGC: Clear dismissal flag on page load/refresh so popup shows again after refresh
  useEffect(() => {
    // AGC: Check if this is a fresh page load (not just a navigation)
    const pageLoadTimestamp = sessionStorage.getItem('page_load_timestamp')
    const currentTimestamp = Date.now().toString()
    
    // AGC: If no timestamp or timestamp is old (more than 1 second ago), it's a fresh page load
    if (!pageLoadTimestamp || (Date.now() - parseInt(pageLoadTimestamp)) > 1000) {
      // AGC: Clear dismissal flag on fresh page load/refresh
      sessionStorage.removeItem('tournament_popup_dismissed')
      sessionStorage.setItem('page_load_timestamp', currentTimestamp)
    }
  }, [])

  const serverStatusLabel = isOnline ? 'server online' : isChecking ? 'checking...' : 'server offline'
  const serverStatusClass = isOnline ? 'text-emerald-400' : isChecking ? 'text-yellow-400' : 'text-red-400'
  const ogbBalanceDisplay = formatOgbBalanceDisplay(ogbBalance)

  // AGC: Listen for tournament events
  useEffect(() => {
    if (!socket) return

    const handleTournamentCreated = (data: Tournament) => {
      console.log('🏆 Tournament created in TopNav:', data)
      setTournament(data)
      // AGC: Only show popup if not dismissed in sessionStorage (clears on page refresh)
      const dismissed = sessionStorage.getItem('tournament_popup_dismissed')
      if (dismissed !== 'true') {
        setShowPopup(true)
      }
    }

    const handleTournamentUpdated = (data: { tournament: Tournament; participants?: Participant[] }) => {
      if (data.tournament) {
        console.log('🏆 TopNav: Tournament updated:', data.tournament.status)
        setTournament(data.tournament)
        if (data.participants) {
          setParticipants(data.participants)
        }
        // AGC: Show popup if tournament is waiting for players or in countdown (and not dismissed)
        // AGC: sessionStorage persists during navigation but clears on page refresh
        const dismissed = sessionStorage.getItem('tournament_popup_dismissed')
        if ((data.tournament.status === 'WAITING_FOR_PLAYERS' || data.tournament.status === 'COUNTDOWN') && dismissed !== 'true') {
          console.log('🏆 TopNav: Showing popup for tournament')
          setShowPopup(true)
        } else {
          setShowPopup(false)
        }
      }
    }

    socket.on('tournament_created', handleTournamentCreated)
    socket.on('tournament_updated', handleTournamentUpdated)

    // AGC: Request current tournament on mount
    socket.emit('get_current_tournament')

    return () => {
      socket.off('tournament_created', handleTournamentCreated)
      socket.off('tournament_updated', handleTournamentUpdated)
    }
  }, [socket])

  // AGC: Countdown timer for minimized indicator
  useEffect(() => {
    if (!tournament) {
      setTimeRemaining(0)
      return
    }
    
    const interval = setInterval(() => {
      if (tournament.status === 'WAITING_FOR_PLAYERS') {
        setTimeRemaining(Math.max(0, tournament.autoStartAt - Date.now()))
      } else if (tournament.status === 'COUNTDOWN' && tournament.countdownEndsAt) {
        setTimeRemaining(Math.max(0, tournament.countdownEndsAt - Date.now()))
      } else {
        setTimeRemaining(0)
      }
    }, 1000)
    
    return () => clearInterval(interval)
  }, [tournament])

  // AGC: Check if current player is in tournament
  const isPlayerInTournament = address && participants.some(p => p.playerId.toLowerCase() === address.toLowerCase())

  // AGC: Format time remaining
  const formatTime = (ms: number) => {
    const minutes = Math.floor(ms / 60000)
    const seconds = Math.floor((ms % 60000) / 1000)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  // AGC: P2P Pending Match countdown timer
  const [p2pTimeRemaining, setP2pTimeRemaining] = useState<number>(0)
  
  useEffect(() => {
    if (!p2pPendingMatch) {
      setP2pTimeRemaining(0)
      return
    }
    
    // AGC: Calculate time remaining from 3-minute expiration
    const updateTime = () => {
      const remaining = Math.max(0, p2pPendingMatch.expiresAt - Date.now())
      setP2pTimeRemaining(remaining)
    }
    
    updateTime()
    const interval = setInterval(updateTime, 1000)
    
    return () => clearInterval(interval)
  }, [p2pPendingMatch])
  
  // AGC: Show P2P indicator when player has a pending match and NOT on P2P page
  const showP2PIndicator = Boolean(
    p2pPendingMatch &&
    p2pTimeRemaining > 0 &&
    activePage !== 'p2pBattle'
  )
  
  // AGC: P2P Active Battle (in-battle) indicator
  const showActiveBattleIndicator = Boolean(
    p2pActiveBattle &&
    activePage !== 'p2pBattle'
  )

  const showCountdownIndicator =
    Boolean(
      tournament &&
      isPlayerInTournament &&
      (tournament.status === 'WAITING_FOR_PLAYERS' || tournament.status === 'COUNTDOWN')
    )

  // AGC: Check for tournaments whenever page changes (home, games, etc.) or when socket becomes available
  useEffect(() => {
    if (!socket) return

    // AGC: Request current tournament when:
    // 1. Navigating to home, games, or tournament pages
    // 2. Socket becomes available (initial load)
    if (!activePage || activePage === 'home' || activePage === 'games' || activePage === 'tournament') {
      console.log(`🔍 TopNav: Checking for tournaments (socket ready, page: ${activePage || 'initial'})...`)
      socket.emit('get_current_tournament')
    }
  }, [socket, activePage])

  // AGC: Close network dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (networkDropdownRef.current && !networkDropdownRef.current.contains(event.target as Node)) {
        setShowNetworkDropdown(false)
      }
    }

    if (showNetworkDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showNetworkDropdown])

  // AGC: Get current network name
  const getCurrentNetworkName = () => {
    if (chainId === baseSepolia.id) return 'Base Sepolia'
    if (chainId === base.id) return 'Base'
    return `Chain ${chainId}`
  }

  // AGC: Handle network switch
  const handleSwitchNetwork = async (targetChainId: number) => {
    if (chainId === targetChainId) {
      setShowNetworkDropdown(false)
      return
    }

    try {
      playSoundSafe('/sounds/click.mp3', 0.3)
      await switchChain({ chainId: targetChainId })
      setShowNetworkDropdown(false)
    } catch (error) {
      console.error('Failed to switch network:', error)
      // AGC: User might have rejected the switch in their wallet
      setShowNetworkDropdown(false)
    }
  }

  return (
    <>
      <nav data-tour="top-nav" className="fixed top-0 left-0 w-full h-[60px] px-4 flex items-center justify-between bg-slate-900/80 backdrop-blur-md border-b border-slate-800/70 z-50">
        {/* AGC: Profile button and settings - Clean icons without background */}
        <div className="flex items-center gap-3">
          <button
            data-tour="profile"
            onClick={() => {
              playSoundSafe('/sounds/click.mp3', 0.3)
              onOpenProfile()
            }}
            className="hover:opacity-70 transition-opacity"
            aria-label="Open Profile"
          >
            <img src={`${import.meta.env.BASE_URL}profile1.png`} alt="Profile" className="w-9 h-9 object-contain" />
          </button>
          <button
            data-tour="settings"
            onClick={() => {
              playSoundSafe('/sounds/click.mp3', 0.3)
              onOpenSettings()
            }}
            className="hover:opacity-70 transition-opacity"
            aria-label="Open settings"
          >
            <img src={`${import.meta.env.BASE_URL}setting.Png`} alt="Settings" className="w-7 h-7 object-contain" />
          </button>
        </div>

        {/* AGC: Network label and server status - Tournament popup appears here */}
        {/* AGC: Emergency Withdraw Button (replaces network label when enabled) - Centered horizontally */}
        {/* AGC: Wrapper with extra space below for dropdown visibility in tour (v6.9.0) */}
        <div data-tour="network-server-area" className="flex flex-col items-center leading-none relative" style={{ paddingBottom: '120px', marginBottom: '-120px' }}>
          {/* AGC: Tournament popup - slides down from top (only show if player hasn't joined AND music popup is not expanded) */}
          {/* AGC: Positioned in same location as music popup but with lower z-index (z-40 vs z-50) */}
          {showPopup && tournament && !isPlayerInTournament && !isHiding && !isMusicPopupExpanded && (
            <div 
              className={`absolute top-full mt-2 left-1/2 transform -translate-x-1/2 z-40 ${
                isHiding ? 'animate-slideUp' : 'animate-slideDown'
              }`}
            >
              <div className="relative bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg shadow-lg border-2 border-yellow-400 p-1"
                style={{
                  animation: isHiding ? undefined : 'blinkBorder 2s infinite'
                }}
              >
                {/* AGC: Close button (X) */}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    playSoundSafe('/sounds/click.mp3', 0.3)
                    setIsHiding(true)
                    // AGC: Use sessionStorage - persists during navigation, clears on page refresh
                    sessionStorage.setItem('tournament_popup_dismissed', 'true')
                    setTimeout(() => {
                      setShowPopup(false)
                      setIsHiding(false)
                    }, 300)
                  }}
                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 text-white text-xs font-bold flex items-center justify-center transition-colors z-10"
                  aria-label="Close popup"
                >
                  ×
                </button>
                
                {/* AGC: Join button - joins active tournament and navigates to tournament page */}
                <button
                  onClick={() => {
                    playSoundSafe('/sounds/click.mp3', 0.4)
                    // AGC: Hide popup with slide-up animation
                    setIsHiding(true)
                    // AGC: Mark as dismissed so it doesn't show again
                    sessionStorage.setItem('tournament_popup_dismissed', 'true')
                    // AGC: Set active tournament ID and navigate to tournament page
                    setTimeout(() => {
                      setShowPopup(false)
                      setIsHiding(false)
                      // AGC: Set the active tournament ID so TournamentLobby shows it (not list)
                      if (tournament && onSelectTournament) {
                        onSelectTournament(tournament.id)
                      }
                      // AGC: Navigate to tournament page
                      onOpenTournament()
                    }, 300)
                  }}
                  className="flex items-center gap-2 text-white font-bold px-4 py-2 transition-all active:scale-95 whitespace-nowrap w-full"
                >
                  <Trophy className="w-5 h-5" />
                  <span>JOIN CONTEST</span>
                </button>
              </div>
            </div>
          )}

          {/* AGC: Minimized tournament indicator - shows when player is in tournament */}
          {showCountdownIndicator && (
            <button
              onClick={() => {
                playSoundSafe('/sounds/click.mp3', 0.3)
                onOpenTournament()
              }}
              className="mb-1 flex items-center gap-2 text-white text-[10px] font-semibold bg-slate-800/95 border border-yellow-500/40 rounded-full px-3 py-1 shadow-lg hover:opacity-85 transition-opacity"
              aria-label="Open active tournament"
            >
              <Trophy className="w-3.5 h-3.5 text-yellow-400" />
              <span className="text-yellow-300">{participants.length}/{tournament?.maxPlayers || 0}</span>
              <span className="text-slate-400">•</span>
              <span className="text-emerald-400">{formatTime(timeRemaining)}</span>
            </button>
          )}
          
          {/* AGC: Minimized P2P pending match indicator - shows when player has pending match */}
          {showP2PIndicator && onOpenP2PBattle && (
            <button
              onClick={() => {
                playSoundSafe('/sounds/click.mp3', 0.3)
                onOpenP2PBattle()
              }}
              className="mb-1 flex items-center gap-2 text-white text-[10px] font-semibold bg-slate-800/95 border border-blue-500/40 rounded-full px-3 py-1 shadow-lg hover:opacity-85 transition-opacity animate-pulse"
              aria-label="Return to P2P match"
            >
              <Swords className="w-3.5 h-3.5 text-blue-400" />
              <span className="text-blue-300">PvP Match</span>
              <span className="text-slate-400">•</span>
              <span className={`${p2pTimeRemaining < 60000 ? 'text-red-400' : 'text-emerald-400'}`}>
                {formatTime(p2pTimeRemaining)}
              </span>
              {p2pPendingMatch?.hasPaid && (
                <>
                  <span className="text-slate-400">•</span>
                  <span className="text-green-400">Paid ✓</span>
                </>
              )}
            </button>
          )}
          
          {/* AGC: Minimized P2P active battle indicator - shows when player is in a battle */}
          {showActiveBattleIndicator && onOpenP2PBattle && p2pActiveBattle && (
            <button
              onClick={() => {
                playSoundSafe('/sounds/click.mp3', 0.3)
                onOpenP2PBattle()
              }}
              className="mb-1 flex items-center gap-2 text-white text-[10px] font-semibold bg-gradient-to-r from-red-900/95 to-orange-900/95 border border-red-500/60 rounded-full px-3 py-1 shadow-lg hover:opacity-85 transition-opacity animate-pulse"
              aria-label="Return to active battle"
            >
              <Swords className="w-3.5 h-3.5 text-red-400" />
              <span className="text-orange-300">Turn {p2pActiveBattle.currentTurn}</span>
              <span className="text-slate-400">•</span>
              <span className={`${p2pActiveBattle.turnTimeLeft <= 15 ? 'text-red-400 animate-pulse' : 'text-emerald-400'}`}>
                {p2pActiveBattle.turnTimeLeft}s
              </span>
              <span className="text-slate-400">•</span>
              <span className="text-yellow-400">⚔️ LIVE</span>
            </button>
          )}
          
          {/* AGC: Emergency Withdraw Popup (with two buttons - one for each contract) */}
          {showEmergencyWithdrawPopup && anyButtonEnabled && (onOpenEmergencyWithdrawStakingManager || onOpenEmergencyWithdrawStakingRevenueShare) && !isEmergencyWithdrawHiding && (
            <div 
              className={`absolute top-full mt-2 left-1/2 transform -translate-x-1/2 z-50 ${
                isEmergencyWithdrawHiding ? 'animate-slideUp' : 'animate-slideDown'
              }`}
            >
              <div className="relative bg-gradient-to-r from-red-600 to-red-700 rounded-lg shadow-lg border-2 border-red-400 p-2 min-w-[200px]"
                style={{
                  animation: isEmergencyWithdrawHiding ? undefined : 'blinkBorder 2s infinite'
                }}
              >
                {/* AGC: Close button (X) */}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    playSoundSafe('/sounds/click.mp3', 0.3)
                    setIsEmergencyWithdrawHiding(true)
                    sessionStorage.setItem('emergency_withdraw_popup_dismissed', 'true')
                    setTimeout(() => {
                      setShowEmergencyWithdrawPopup(false)
                      setIsEmergencyWithdrawHiding(false)
                    }, 300)
                  }}
                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 text-white text-xs font-bold flex items-center justify-center transition-colors z-10"
                  aria-label="Close popup"
                >
                  ×
                </button>
                
                {/* AGC: Title */}
                <p className="text-white text-xs font-bold text-center mb-2 flex items-center justify-center gap-1 whitespace-nowrap">
                  <span>🚨</span>
                  <span>EMERGENCY WITHDRAW</span>
                </p>
                
                {/* AGC: Two buttons - one for each contract */}
                <div className="flex flex-col gap-1">
                  {/* AGC: StakingManager button (2-of-2 approval) */}
                  {onOpenEmergencyWithdrawStakingManager && stakingManagerButtonEnabled && (
                    <button
                      onClick={() => {
                        playSoundSafe('/sounds/click.mp3', 0.4)
                        setIsEmergencyWithdrawHiding(true)
                        sessionStorage.setItem('emergency_withdraw_popup_dismissed', 'true')
                        setTimeout(() => {
                          setShowEmergencyWithdrawPopup(false)
                          setIsEmergencyWithdrawHiding(false)
                          onOpenEmergencyWithdrawStakingManager()
                        }, 300)
                      }}
                      className="flex items-center justify-center gap-2 text-white font-bold px-3 py-2 bg-orange-600 hover:bg-orange-700 rounded-lg transition-all active:scale-95 whitespace-nowrap text-xs"
                    >
                      <span>📊</span>
                      <span>StakingManager</span>
                    </button>
                  )}
                  
                  {/* AGC: StakingRevenueShare button (1 signer approval) */}
                  {onOpenEmergencyWithdrawStakingRevenueShare && stakingRevenueShareButtonEnabled && (
                    <button
                      onClick={() => {
                        playSoundSafe('/sounds/click.mp3', 0.4)
                        setIsEmergencyWithdrawHiding(true)
                        sessionStorage.setItem('emergency_withdraw_popup_dismissed', 'true')
                        setTimeout(() => {
                          setShowEmergencyWithdrawPopup(false)
                          setIsEmergencyWithdrawHiding(false)
                          onOpenEmergencyWithdrawStakingRevenueShare()
                        }, 300)
                      }}
                      className="flex items-center justify-center gap-2 text-white font-bold px-3 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-all active:scale-95 whitespace-nowrap text-xs"
                    >
                      <span>💰</span>
                      <span>RevenueShare</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* AGC: Network switcher dropdown (always visible, emergency withdraw popup appears above it) */}
          <div className="relative" ref={networkDropdownRef}>
              {/* AGC: Music button - positioned over network text */}
              <MusicButton onExpandedChange={setIsMusicPopupExpanded} />
              
              <button
                data-tour="network-button"
                onClick={() => {
                  playSoundSafe('/sounds/click.mp3', 0.3)
                  setShowNetworkDropdown(!showNetworkDropdown)
                }}
                disabled={isSwitchingChain}
                className="flex items-center gap-1 text-xs font-semibold text-white/90 hover:text-white transition-colors disabled:opacity-50"
                aria-label="Switch network"
              >
                {isSwitchingChain ? 'Switching...' : getCurrentNetworkName()}
                <ChevronDown className={`w-3 h-3 transition-transform ${showNetworkDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {/* AGC: Network dropdown menu */}
              {showNetworkDropdown && (
                <div className="absolute top-full mt-2 left-1/2 transform -translate-x-1/2 z-50 bg-slate-800 border border-slate-600 rounded-lg shadow-lg min-w-[160px] overflow-hidden">
                  <button
                    data-tour="network-sepolia"
                    onClick={() => handleSwitchNetwork(baseSepolia.id)}
                    className={`w-full px-4 py-2 text-left text-sm font-medium transition-colors ${
                      chainId === baseSepolia.id
                        ? 'bg-blue-600 text-white'
                        : 'text-white/90 hover:bg-slate-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>Base Sepolia</span>
                      {chainId === baseSepolia.id && (
                        <span className="text-xs">✓</span>
                      )}
                    </div>
                    <div className="text-xs text-white/60 mt-0.5">Testnet</div>
                  </button>
                  <button
                    data-tour="network-mainnet"
                    onClick={() => handleSwitchNetwork(base.id)}
                    className={`w-full px-4 py-2 text-left text-sm font-medium transition-colors ${
                      chainId === base.id
                        ? 'bg-blue-600 text-white'
                        : 'text-white/90 hover:bg-slate-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>Base</span>
                      {chainId === base.id && (
                        <span className="text-xs">✓</span>
                      )}
                    </div>
                    <div className="text-xs text-white/60 mt-0.5">Mainnet</div>
                  </button>
                </div>
              )}
            </div>
          <span data-tour="server-status" className={`text-[10px] ${serverStatusClass}`}>{serverStatusLabel}</span>
        </div>

        {/* AGC: OGB balance pill */}
        <button
          data-tour="wallet-button"
          onClick={() => {
            playSoundSafe('/sounds/click.mp3', 0.3)
            onOpenInventory()
          }}
          className="flex items-center gap-1 rounded-lg bg-indigo-600/80 px-3 py-1.5 text-xs font-semibold shadow-lg shadow-indigo-500/30 border border-indigo-400/30 hover:bg-indigo-700"
          aria-label="View OGB balance"
        >
          <img src={`${import.meta.env.BASE_URL}coiin.Png`} alt="OGB Token" className="w-4 h-4 object-contain" />
          {ogbBalanceDisplay}
        </button>
      </nav>

      {/* AGC: CSS animations for tournament popup */}
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translate(-50%, -20px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
        
        @keyframes slideUp {
          from {
            opacity: 1;
            transform: translate(-50%, 0);
          }
          to {
            opacity: 0;
            transform: translate(-50%, -20px);
          }
        }
        
        @keyframes blinkBorder {
          0%, 100% {
            border-color: rgb(250, 204, 21);
            box-shadow: 0 0 10px rgba(250, 204, 21, 0.5);
          }
          50% {
            border-color: rgb(251, 191, 36);
            box-shadow: 0 0 20px rgba(251, 191, 36, 0.8);
          }
        }
        
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
        
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </>
  )
}

